import React from 'react';
import JSON5 from 'json5';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import {
  getPeriodText,
  parsePeriodValuesString,
} from '../../ReportBuilder/Components/SectionPeriod/utils';
import { formattedDate } from 'shared/utils/date';
import RenderLocationAnswer from 'donor/portal/pages/FeedbackDashboard/Feedback/RenderLocationAnswer';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import {
  FinalBudget,
  getBudgetDollarAmount,
} from '../../ReportBuilder/Components/Budget/utils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { Beneficiaries } from '../../ReportBuilder/Components/Beneficiary/utils/beneficiaryInterfaces';
import {
  IndicatorByService,
  indicatorSectionsInOrder,
} from '../../ReportBuilder/Components/ImpactIndicator/utils';
import { sectionKeyToString } from '../../ReportBuilder/Components/IndicatorDetails/utils';
import { TrueImpactFile } from 'shared/design-system/components/TrueImpactFileUploadBox';
import TrueImpactFileRender from 'shared/design-system/components/TrueImpactFileRender';
import BeneficiaryCard, {
  beneficiaryCategories,
} from '../../ReportBuilder/Components/ReportFinalize/FinalizeBeneficiaries/BeneficiaryCard';
import { defaultCostValue } from '../../ReportBuilder/Components/Budget/RelevancyCheckbox';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { isSignificantInvestmentAnswer } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import {
  BENEFICIARY_QUESTION_ID,
  NARRATIVE_LESSON_QUESTION_ID,
  NARRATIVE_STORY_QUESTION_ID,
  parseLessonValue,
  parseStoryValue,
} from '../../ReportBuilder/Components/Narrative/utils';

const getBudgetSummaryValues = (
  questionId: number,
  budgetSummary: FinalBudget,
): string => {
  switch (questionId) {
    case 80003:
      return budgetSummary.directCost || budgetSummary[80003];
    case 80005:
      return budgetSummary.inKindDonations || budgetSummary[80005];
    case 80007:
      return budgetSummary.overheadCost || budgetSummary[80007];

    default:
      return '';
  }
};

const getBudgetText = (questionId: number) => {
  switch (questionId) {
    case 80003:
      return 'Direct Cost';
    case 80005:
      return 'In Kind Donations';
    case 80007:
      return 'Overhead Cost';
  }
};

const renderBudgetAmount = (amount?: string) => {
  if (amount === undefined || amount === defaultCostValue) {
    return 'N/A';
  }
  let value: string = amount;
  try {
    const parsed = JSON5.parse(amount);
    if (parsed && typeof parsed === 'object' && 'currency' in parsed) {
      value = String((parsed as { currency: string }).currency);
    }
  } catch {
    // Not JSON — treat as a plain numeric string.
  }
  if (value === defaultCostValue) {
    return 'N/A';
  }
  return `$${numberWithCommas(value)}`;
};

export const parseAnswer = (answer: Answer | { text: string }) => {
  try {
    return JSON5.parse(answer.text);
  } catch (e) {
    return undefined;
  }
};

export const getAnswerText = (
  questionId: number,
  answer: Answer | { text: string },
): JSX.Element | string | undefined => {
  switch (questionId) {
    case 1:
    case 10004:
    case 20004:
    case 20008:
    case 20016:
    case 40002:
    case 40003:
    case 40004:
    case 40006:
    case 40007:
    case 40008:
    case 40009:
    case 40022:
    case 10001:
    case 10002:
    case 80001:
    case 110001:
    case 110002:
    case 110003:
    case 110004:
    case 110005:
    case 110006:
      return answer.text;

    case 10006:
      return formattedDate(answer.text);

    case 50001:
      return (
        <TrueImpactText>
          Please review the impact model on the left.
        </TrueImpactText>
      );

    case 20009: {
      const isSignificant = isSignificantInvestmentAnswer(answer);
      if (isSignificant) {
        return (
          <>
            {answer.text}
            <span
              style={{
                fontWeight: 'bold',
                color: COLORS.grayBase,
              }}
            >
              {' '}
              (Significant investment)
            </span>
          </>
        );
      }
      return answer.text;
    }

    case 40015: {
      const impactSummary: IndicatorByService = parseAnswer(answer);
      if (!impactSummary) {
        return undefined;
      }
      return (
        <>
          {indicatorSectionsInOrder.map(section => {
            const s = impactSummary[section];
            if (!s.length) return null;
            return (
              <div key={section}>
                <TrueImpactText variant="body-small-bold" className="mb-1">
                  {sectionKeyToString(section)}:
                </TrueImpactText>
                {s.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      marginLeft: SPACERS.small,
                      marginBottom: SPACERS.tiny,
                    }}
                  >
                    <TrueImpactText>{item.name}</TrueImpactText>
                    <TrueImpactText>
                      Definition: {item.updatedText}
                    </TrueImpactText>
                  </div>
                ))}
              </div>
            );
          })}
        </>
      );
    }

    case BENEFICIARY_QUESTION_ID: {
      const beneficiary: Beneficiaries = parseAnswer(answer);
      if (!beneficiary) {
        return undefined;
      }
      return (
        <div className="beneficiary-cards-wrapper">
          {beneficiaryCategories.map(category => (
            <BeneficiaryCard
              key={category}
              category={category}
              beneficiaries={beneficiary}
            />
          ))}
        </div>
      );
    }

    case 80003:
    case 80005:
    case 80007: {
      const budget = getBudgetDollarAmount(answer.text);
      if (!budget) {
        const budgetSummary = parseAnswer(answer);
        if (!budgetSummary) {
          return undefined;
        }
        return (
          <TrueImpactText>
            {renderBudgetAmount(
              getBudgetSummaryValues(questionId, budgetSummary),
            )}
          </TrueImpactText>
        );
      }
      return (
        <TrueImpactText>
          {getBudgetText(questionId)}:{' '}
          <strong>{renderBudgetAmount(budget)}</strong>
        </TrueImpactText>
      );
    }

    case 80011: {
      const budgetSummary = parseAnswer(answer) as FinalBudget;
      if (!budgetSummary) {
        return undefined;
      }
      const directCost = getBudgetSummaryValues(80003, budgetSummary);
      const overheadCost = getBudgetSummaryValues(80007, budgetSummary);
      const inKindDonations = getBudgetSummaryValues(80005, budgetSummary);
      return (
        <TrueImpactText className="budget-summary-render">
          <div className="mb-1">Budget Summary:</div>
          <div>
            Direct Cost: <strong>{renderBudgetAmount(directCost)}</strong>
          </div>
          <div>
            Overhead Cost: <strong>{renderBudgetAmount(overheadCost)}</strong>
          </div>
          <div>
            In Kind Donations:{' '}
            <strong>{renderBudgetAmount(inKindDonations)}</strong>
          </div>
        </TrueImpactText>
      );
    }

    case 80009: {
      const budgetFile: {
        file?: TrueImpactFile;
        files?: TrueImpactFile;
        fileName?: string;
        description: string;
      } = parseAnswer(answer);
      if (!budgetFile) {
        return undefined;
      }
      return (
        <TrueImpactText className="budget-file-render">
          <div className="mb-1">
            <strong>Budget File:</strong>
          </div>
          <div>{budgetFile.description}</div>
          <TrueImpactFileRender
            style={{
              padding: SPACERS.small,
            }}
            file={budgetFile.file || budgetFile.fileName || budgetFile.files}
          />
        </TrueImpactText>
      );
    }

    case 30017:
      return <RenderLocationAnswer parseObject={JSON5.parse(answer.text)} />;

    case 40010:
    case 40011:
    case 40021: {
      const r: string[] | undefined = parseAnswer(answer);
      if (!r) {
        return undefined;
      }
      return (
        <TrueImpactText className="impact-summary-array-render">
          {r.join(', ')}
        </TrueImpactText>
      );
    }

    case 10003: {
      const period = parsePeriodValuesString(answer.text);
      if (!period.startDate || !period.endDate || !period.card) {
        return undefined;
      }
      return (
        <>
          {getPeriodText(period.card)}:{' '}
          <strong>{formattedDate(period.startDate)}</strong> -{' '}
          <strong>{formattedDate(period.endDate)}</strong>
        </>
      );
    }

    case NARRATIVE_STORY_QUESTION_ID: {
      const story = parseStoryValue(answer.text);
      return (
        <>
          <div className="mb-1">{story.storyTitle}</div>
          <div className="mb-1">{story.storyDescription}</div>
          <img
            style={{ maxWidth: 250 }}
            src={story.storyFileUrl}
            alt={story.storyTitle}
            title={story.storyTitle}
          />
        </>
      );
    }

    case NARRATIVE_LESSON_QUESTION_ID: {
      const lesson = parseLessonValue(answer.text);
      return (
        <>
          <div className="mb-1">{lesson.lessonDescription}</div>
          <div>{lesson.storyLesson.join(', ')}</div>
        </>
      );
    }

    case 50050:
    case 60050:
    case 110050: {
      const note = parseAnswer(answer);
      if (!note || typeof note !== 'object') {
        return answer.text;
      }
      return (
        <>
          <div className="mb-1">{note.description}</div>
          <TrueImpactFileRender
            style={{
              padding: SPACERS.small,
            }}
            file={note.files || note.fileName || note.file}
          />
        </>
      );
    }

    default:
      return answer.text;
  }
};

interface FeedbackAnswerProps {
  questionId: number;
  answer: Answer;
  className?: string;
}

const StyledDiv = styled.div`
  .mb-1 {
    margin-bottom: ${SPACERS.tiny};
  }
  .beneficiary-cards-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: ${SPACERS.small} ${SPACERS.large};
    .beneficiary-card {
      margin-bottom: 0;
      .age-text {
        margin-bottom: ${SPACERS.extraTiny};
      }
      .card-details-heading {
        font-size: 14px;
        margin-bottom: ${SPACERS.tiny};
      }
      .card-row {
        margin-bottom: ${SPACERS.tiny};
        & * {
          font-size: 12px;
        }
      }
    }
  }
`;

const FeedbackAnswer: React.FC<FeedbackAnswerProps> = ({
  questionId,
  answer,
  className,
}) => {
  const a = getAnswerText(questionId, answer);
  if (!a) {
    return (
      <TrueImpactText className={className} color="critical">
        This question has not been answered.
      </TrueImpactText>
    );
  }
  return <StyledDiv className={className}>{a}</StyledDiv>;
};

export default FeedbackAnswer;
